import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/lib/supabase";
import { validatePassword } from "@/utils/passwordValidation";
import { PasswordRequirements } from "@/components/auth/PasswordRequirements";
import { Spinner } from "@/components/ui/spinner";
import { ErrorHandler, ErrorCategory, ErrorSeverity } from "@/utils/errorHandler";
import { ArrowLeft, AlertTriangle, ExternalLink } from "lucide-react";

// Constants for direct API access - using explicit constants to avoid any API interceptor issues
const SUPABASE_URL = "https://muoihzfomfercsujyyta.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im11b2loemZvbWZlcmNzdWp5eXRhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM4Nzc5ODYsImV4cCI6MjA1OTQ1Mzk4Nn0.yOlwlmjSRiqWhjEPWgCg_jvqLEgvfzQacDhezJlQZ98";

// Debug logging utility - only logs in development
const debugLog = (message: string, data?: any) => {
  if (import.meta.env.MODE === 'development') {
    console.log(message, data);
  }
};

// Simplified type definitions
interface InvitationData {
  email: string;
  organization_id: string;
  family_id: string;
}

interface UserProfile {
  id: string;
  email: string | null;
  organization_id: string;
  role: string;
  is_superuser: boolean | null;
  invited_at: string | null;
  subscription_status: string | null;
  created_at: string;
  onboarding_steps_completed?: string[];
}

interface ParentInvitation {
  id: string;
  email: string;
  organization_id: string;
  family_id: string;
  expires_at: string;
  accepted_at: string | null;
  created_by: string | null;
  created_at: string;
}

const SetPassword = () => {
  // Add a top-level debug log to confirm component render
  debugLog('[DEBUG] SetPassword component rendered');
  // State management
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [invitation, setInvitation] = useState<ParentInvitation | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [invitationType, setInvitationType] = useState<'staff' | 'parent' | null>(null);
  const [showSuccessSpinner, setShowSuccessSpinner] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Error handler
  const errorHandler = new ErrorHandler("SetPassword");

  // Define token validation function
  const validateInvitation = async () => {
    try {
      // Check for token in URL
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get("token");
      const inviteType = urlParams.get("type"); // Get invitation type from URL
      
      if (!token) {
        throw new Error("No invitation token found");
      }
      
      // Determine which validation function to use based on type
      const functionName = inviteType === 'staff' ? 'validate-staff-invite' : 'validate-invite';
      const apiUrl = `${SUPABASE_URL}/functions/v1/${functionName}?token=${encodeURIComponent(token)}`;
      
      try {
        debugLog(`[DEBUG] About to call directFetch for ${functionName}:`, apiUrl);
        const data = await directFetch(
          apiUrl,
          {
            method: 'GET'
          },
          30000 // 30 second timeout
        );
        debugLog(`[DEBUG] directFetch completed for ${functionName}:`, data);
        
        if (!data.success || !data.invitation) {
          throw new Error(data.error || "Invalid invitation");
        }
        
        // Save invitation data
        setInvitation(data.invitation);
        setEmail(data.invitation.email);
        setInvitationType(inviteType === 'staff' ? 'staff' : 'parent');
        
        setValidatingToken(false);
      } catch (fetchError: any) {
        setError(fetchError.message || "Invalid or expired invitation link");
      }
    } 
    catch (error: any) {
      setError(error.message || "Invalid or expired invitation link");
    } 
    finally {
      // Always set validatingToken to false to avoid infinite spinner
      setValidatingToken(false);
    }
  };

  // Validate invitation token when component mounts
  useEffect(() => {
    let isMounted = true;
    
    // Run validation with a small delay to ensure component is fully mounted
    const timeoutId = setTimeout(() => {
      if (isMounted) validateInvitation();
    }, 500);
    
    // Cleanup 
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [location, retryCount]);

  const handleSetPassword = async (e: React.FormEvent) => {
    debugLog('[DEBUG] handleSetPassword CALLED');
    e.preventDefault();
    setError(null);
    debugLog('[DEBUG] Passwords entered - validation check');
    // Validate passwords
    if (password !== confirmPassword) {
      debugLog('[DEBUG] Passwords do not match');
      setError("Passwords do not match");
      return;
    }
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      debugLog('[DEBUG] Password validation failed:', passwordValidation.message);
      setError(passwordValidation.message);
      return;
    }
    setLoading(true);
    try {
      // Get token from URL
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get("token");
      const inviteType = urlParams.get("type"); // Get invitation type from URL
      
      if (!token) {
        debugLog('[DEBUG] Missing token parameter');
        throw new Error("Missing token parameter");
      }
      
      // Determine which validation function to use based on type
      const functionName = inviteType === 'staff' ? 'validate-staff-invite' : 'validate-invite';
      const apiUrl = `${SUPABASE_URL}/functions/v1/${functionName}`;
      
      const requestBody = { 
        token: token, 
        password: password 
      };
      
      debugLog(`[DEBUG] About to POST to ${functionName} - request prepared`);
      const data = await directFetch(
        apiUrl,
        {
          method: 'POST',
          body: JSON.stringify(requestBody),
          headers: {
            'Content-Type': 'application/json'
          }
        },
        30000
      );
      debugLog(`[DEBUG] POST response from ${functionName}:`, data);
      if (!data.success) {
        debugLog('[DEBUG] POST returned error:', data.error);
        throw new Error(data.error || "Failed to complete account setup");
      }
      toast.success("Account setup complete", {
        description: "Your password has been set. You can now sign in."
      });
      setShowSuccessSpinner(true);
      
      setTimeout(() => {
        navigate("/auth");
      }, 2000);
    } 
    catch (error: any) {
      debugLog('[DEBUG] Error in handleSetPassword:', error);
      setError(error.message || "An error occurred. Please try again.");
    } 
    finally {
      setLoading(false);
    }
  };

  // Save a reference to the original fetch before any patching
  const originalFetch = window.fetch;

  // Function that completely bypasses API interceptor with built-in timeout
  const directFetch = async (url: string, options: RequestInit = {}, timeoutMs: number = 15000) => {
    const startTime = Date.now();
    // Don't log full URLs that might contain tokens
    const sanitizedUrl = url.includes('?') 
      ? url.substring(0, url.indexOf('?')) + '?[params-redacted]' 
      : url;
    
    // Create an AbortController for the timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);
    
    try {
      // Add security headers
      const securityHeaders = {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Cache-Control': 'no-store, no-cache, must-revalidate',
        'Pragma': 'no-cache',
        ...(options.headers || {})
      };
      
      // Merge the signal from AbortController with any existing options
      const fetchOptions = {
        ...options,
        signal: controller.signal,
        headers: securityHeaders,
        cache: 'no-store' as RequestCache
      };
      fetchOptions.method = options.method || 'GET';
      
      // Create a sanitized version for logging (remove password from body)
      const sanitizedOptions = {
        ...fetchOptions,
        body: fetchOptions.body ? '[Request body with sensitive data - redacted for security]' : undefined
      };
      debugLog('[DEBUG] fetchOptions before fetch:', sanitizedOptions);
      
      // NEVER log request body for security-sensitive operations
      const logUrl = url.includes('?') ? url.substring(0, url.indexOf('?')) + '?[params-redacted]' : url;
      debugLog('[DEBUG] About to execute fetch:', { url: logUrl, options: sanitizedOptions });
      const response = await originalFetch(url, fetchOptions);
      debugLog('[DEBUG] Fetch executed, got response:', response);
      
      const endTime = Date.now();
      const elapsed = endTime - startTime;
      
      const text = await response.text();
      
      // Don't log response text that might contain sensitive data
      if (!response.ok) {
        try {
          // Try to parse error but without logging the full text
          const errorData = JSON.parse(text);
          const sanitizedError = {
            success: errorData.success,
            error: errorData.error
          };
        } catch (e) {
        }
        
        throw new Error(`Fetch failed with status: ${response.status}`);
      }
      
      try {
        return JSON.parse(text);
      } catch (e: any) {
        return { text };
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error(`Fetch timed out after ${timeoutMs}ms`);
      }
      
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Loading state while validating token
  if (validatingToken) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center py-6">
              <p className="mb-4 text-gray-600">
                Validating invitation...
              </p>
              <Spinner className="mx-auto" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state if validation failed
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-full max-w-md p-8">
          <Card>
            <CardHeader>
              <CardTitle>Something went wrong</CardTitle>
              <CardDescription>
                {typeof error === 'string' && error.toLowerCase().includes('expired')
                  ? 'This invitation link has expired. Please request a new one from your organization.'
                  : 'We were unable to verify your invitation. Please check your link or try again.'}
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex flex-col gap-2">
              <Button variant="default" onClick={() => {
                setError(null);
                setValidatingToken(true);
                setRetryCount(c => c + 1);
                setTimeout(() => validateInvitation(), 100);
              }}>
                Try validation again
              </Button>
              <Button variant="link" onClick={() => navigate("/auth")}>Return to Sign In</Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <CardTitle>Set Your Password</CardTitle>
          <CardDescription>
            Welcome! Please create a password to finish setting up your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-6">
              <p className="mb-4 text-gray-600">Setting up your account...</p>
              <Spinner className="mx-auto" />
            </div>
          ) : showSuccessSpinner ? (
            <div className="text-center py-6">
              <p className="mb-4 text-green-700 font-medium">
                Your account is being set up. Redirecting you to sign in...
              </p>
              <Spinner className="mx-auto" />
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                debugLog('[DEBUG] form onSubmit triggered');
                handleSetPassword(e);
              }}
              className="space-y-4 mt-4"
            >
              {email && (
                <div className="bg-blue-50 border border-blue-100 text-blue-700 px-4 py-3 rounded mb-4 text-center">
                  <p>Account for <strong>{email}</strong></p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error && error.toLowerCase().includes("password")) setError(null);
                  }}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  placeholder="Enter a secure password"
                />
                <PasswordRequirements password={password} />
                {error && error.toLowerCase().includes("password") && (
                  <div className="text-red-600 text-sm mt-1">{error}</div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="Re-enter your password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                Complete Setup
              </Button>
              
              {/* Terms and Privacy Policy Agreement */}
              <div className="text-center mt-4">
                <p className="text-xs text-muted-foreground">
                  By completing setup, you agree to our{" "}
                  <Link 
                    to="/terms-of-service" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link 
                    to="/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </form>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button variant="link" onClick={() => navigate("/auth")}>Return to Sign In</Button>
        </CardFooter>
      </Card>
      {/* Subtle footer for reassurance */}
      <div className="mt-6 text-xs text-gray-400 text-center">
        Your information is secure and will never be shared.
      </div>
    </div>
  );
};

export default SetPassword;
